import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppLayout from './components/layout/AppLayout';
import HomePage from './pages/HomePage';
import WorkspacesPage from './pages/WorkspacesPage';
import WorkspacePage from './pages/WorkspacePage';
import DataSourcesPage from './pages/DataSourcesPage';
import AccountPage from './pages/AccountPage';
import WidgetsPage from './pages/WidgetsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AccessRequestPage from './pages/AccessRequestPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import GoogleCallbackPage from './pages/GoogleCallbackPage';
import NotFoundPage from './pages/NotFoundPage';
import WidgetPreview from './components/embed/WidgetPreview';
import EmbedChatPage from './pages/EmbedChatPage';
import EmbedMapPage from './pages/EmbedMapPage';
import { LayoutProvider } from './context/LayoutContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useDarkMode } from './hooks/useDarkMode';
import { PostHogProvider } from './providers/PostHogProvider';
import CookieBanner from './components/common/CookieBanner';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Customer sites load the chat in an iframe. A top-level visit is the owner preview.
const EmbedChatRoute = () => {
  if (window.parent !== window) return <EmbedChatPage />;
  return (
    <ProtectedRoute>
      <WidgetPreview />
    </ProtectedRoute>
  );
};

function AppContent() {
  const location = useLocation();
  const isEmbed = location.pathname === '/embed' || location.pathname.startsWith('/embed/');

  return (
    <>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/signup" element={<RegisterPage />} />
            <Route path="/request-access" element={<AccessRequestPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/embed" element={<EmbedChatRoute />} />
            <Route path="/embed/map" element={<EmbedMapPage />} />

            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <AppLayout>
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/workspaces" element={<WorkspacesPage />} />
                      <Route path="/widgets" element={<WidgetsPage />} />
                      <Route path="/workspace/:id" element={<WorkspacePage />} />
                      <Route path="/sources" element={<DataSourcesPage />} />
                      <Route path="/functions" element={<Navigate to="/sources?tab=functions" replace />} />
                      <Route path="/profile" element={<Navigate to="/account" replace />} />
                      <Route path="/account" element={<AccountPage />} />
                      <Route path="/billing" element={<Navigate to="/account" replace />} />
                      <Route path="/google/callback" element={<GoogleCallbackPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </AppLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
          {!isEmbed && <CookieBanner />}
    </>
  );
}

function App() {
  // Initialize dark mode (defaults to light, but preserves functionality)
  useDarkMode();

  return (
    <Router>
      <AuthProvider>
        <PostHogProvider>
          <LayoutProvider>
            <AppContent />
          </LayoutProvider>
        </PostHogProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
